import { BreakpointObserver } from '@angular/cdk/layout';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { PlatformService, ThemeService } from '@ld.consumer-portal/utils';
import { map } from 'rxjs';

export interface VerificationCodeSubmit {
  code: string;
  rememberDevice: boolean;
}

@Component({
  selector: 'ld-verification-code-card',
  imports: [
    FormsModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatTooltipModule,
    RouterLink,
    TranslocoDirective,
  ],
  templateUrl: './verification-code-card.component.html',
  styleUrl: './verification-code-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VerificationCodeCardComponent implements AfterViewInit {
  private readonly transloco = inject(TranslocoService);
  private readonly platformService = inject(PlatformService);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly codeInput = viewChild<ElementRef<HTMLInputElement>>('codeInput');

  private readonly isNativeMobile = this.platformService.isNativeMobile;

  private readonly isMobileViewport = toSignal(
    this.breakpointObserver.observe('(max-width: 767px)').pipe(map((result) => result.matches)),
    { initialValue: this.breakpointObserver.isMatched('(max-width: 767px)') },
  );

  readonly isMobile = computed(() => this.isNativeMobile() || this.isMobileViewport());

  readonly showRememberDeviceSheet = signal(false);

  readonly rememberDeviceTooltip = this.transloco.translate('mfaVerificationRememberDeviceTooltip');

  embedded = input<boolean>(false);

  showLogo = input<boolean>(true);

  showTitle = input<boolean>(true);

  maskedContact = input.required<string>();

  verificationType = input<'email' | 'text' | 'call'>('email');

  showRememberDevice = input<boolean>(true);

  showFaqLink = input<boolean>(false);

  accountId = input<string>('');

  protected readonly faqLink = computed(() => {
    const id = this.accountId();
    return id ? `/help/${id}/faqs` : '/dashboard';
  });

  verify = output<VerificationCodeSubmit>();

  resend = output<void>();

  cancel = output<void>();

  private readonly themeService = inject(ThemeService);

  readonly logo = this.themeService.getLogoUrl();

  readonly logoAlt = this.themeService.getPartnerDisplayName();

  verificationCode = signal<string>('');

  rememberDevice = signal<boolean>(false);

  verifyLoading = signal<boolean>(false);

  resendLoading = signal<boolean>(false);

  errorMessage = signal<string>('');

  successMessage = signal<string>('');

  private readonly CODE_LENGTH = 6;

  ngAfterViewInit(): void {
    this.codeInput()?.nativeElement.focus();
  }

  get canVerify(): boolean {
    return this.verificationCode().trim().length === this.CODE_LENGTH && !this.verifyLoading();
  }

  onCodeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.verificationCode.set(input.value);
    this.errorMessage.set('');
  }

  onRememberDeviceChange(checked: boolean): void {
    this.rememberDevice.set(checked);
  }

  onVerify(): void {
    if (this.canVerify) {
      this.verifyLoading.set(true);
      this.errorMessage.set('');
      this.verify.emit({
        code: this.verificationCode().trim(),
        rememberDevice: this.rememberDevice(),
      });
    }
  }

  onResend(): void {
    this.resendLoading.set(true);
    this.errorMessage.set('');
    this.successMessage.set('');
    this.resend.emit();
  }

  onCancel(): void {
    this.cancel.emit();
  }

  openRememberDeviceSheet(): void {
    this.showRememberDeviceSheet.set(true);
  }

  closeRememberDeviceSheet(): void {
    this.showRememberDeviceSheet.set(false);
  }

  setError(message: string): void {
    this.errorMessage.set(message);
    this.verifyLoading.set(false);
  }

  setSuccess(message: string): void {
    this.successMessage.set(message);
    this.resendLoading.set(false);
  }

  setVerifyLoading(loading: boolean): void {
    this.verifyLoading.set(loading);
  }

  setResendLoading(loading: boolean): void {
    this.resendLoading.set(loading);
  }

  clearCode(): void {
    this.verificationCode.set('');
  }

  clearMessages(): void {
    this.errorMessage.set('');
    this.successMessage.set('');
  }

  getMethodDescription(): string {
    switch (this.verificationType()) {
      case 'email':
        return this.transloco.translate('mfaMethodsEmail');
      case 'text':
        return this.transloco.translate('mfaMethodsTextMessage');
      case 'call':
        return this.transloco.translate('mfaMethodsPhoneCall');
      default:
        return this.transloco.translate('mfaMethodsEmail');
    }
  }
}
